import { useCallback, useEffect, useRef, useState } from 'react'

import type { RegisterSettingsDirtyState } from './useSettingsDirtyState'
import { FeatureFlag, useFeatureFlags } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'
import { useRouter } from 'next/router'

import {
  getSettingsSections,
  getVisibleSettingsSections,
} from '../constants/settingsSections'
import type {
  SettingsFormController,
  SettingsSectionId,
  SettingsSectionRegistrations,
} from '../types/types'

const SETTINGS_PATH = '/settings/account-management'
const DEFAULT_SETTINGS_SECTION: SettingsSectionId = 'account'

type PendingNavigation =
  | { sectionId: SettingsSectionId; type: 'section' }
  | { source: 'link'; type: 'route'; url: string }
  | {
      returnState: unknown
      returnUrl: string
      source: 'history'
      type: 'route'
      url: string
    }

export const useSettingsPage = (
  registrations: SettingsSectionRegistrations
) => {
  const router = useRouter()
  const {
    isImplementer,
    shouldEnableUpdateOrganizationSettings,
    shouldShowAnnouncementSettings,
    shouldShowCreateManualIncidentCategory,
    shouldShowCustomerSecretProviderSettings,
    shouldShowEmailSourceSettings,
    shouldShowExternalAccountSettings,
    shouldShowQuickReport,
    shouldShowSettingsPage,
  } = usePermissions()
  const flags = useFeatureFlags()
  const [pendingNavigation, setPendingNavigation] =
    useState<PendingNavigation>()
  const allowNavigationRef = useRef(false)

  // Live form controllers published by mounted section content via
  // useRegisterSettingsDirtyState. Registrations are static objects, so this
  // is the only way a section can drive the shell's Save button and the
  // unsaved-changes guard.
  const [dirtyStateControllers, setDirtyStateControllers] = useState<
    Partial<Record<SettingsSectionId, SettingsFormController>>
  >({})
  const registerDirtyState = useCallback<RegisterSettingsDirtyState>(
    (sectionId, controller) => {
      setDirtyStateControllers((previous) => {
        if (previous[sectionId] === controller) {
          return previous
        }

        if (controller === undefined) {
          if (!(sectionId in previous)) {
            return previous
          }

          const next = { ...previous }
          delete next[sectionId]
          return next
        }

        return { ...previous, [sectionId]: controller }
      })
    },
    []
  )

  const catalogSections = getVisibleSettingsSections(
    getSettingsSections(
      {
        areBrowserNotificationsEnabled:
          flags[FeatureFlag.browserNotifications] ?? false,
        areIncidentAudioAlertsEnabled:
          flags[FeatureFlag.incidentAudioAlerts] ?? false,
        isAiOperatorEnabled: flags[FeatureFlag.aiOperator] ?? false,
        isCreateLlmIncidentsEnabled:
          flags[FeatureFlag.llmProcessedIncidents] ?? false,
        isDatabaseResetEnabled: flags[FeatureFlag.salesDatabaseReset] ?? false,
        isDevelopmentModeEnabled: flags[FeatureFlag.development] ?? false,
        isDeviceHealthSettingsEnabled:
          flags[FeatureFlag.deviceHealthSettings] ?? false,
        isDeviceLabelingEnabled: flags[FeatureFlag.deviceLabeling] ?? false,
        isImplementer,
        isQuickReportEnabled: flags[FeatureFlag.quickReport] ?? false,
        isShowDarkModeToggleEnabled:
          flags[FeatureFlag.showDarkModeToggle] ?? false,
        shouldShowAnnouncementSettings,
        shouldShowCreateManualIncidentCategory,
        // The policy only does anything where incidentAudioAlerts (which gates
        // all incident audio) is on, and only PM and Implementor can see or
        // change it.
        shouldShowCriticalIncidentSoundPolicy:
          (flags[FeatureFlag.incidentAudioAlerts] ?? false) &&
          shouldEnableUpdateOrganizationSettings,
        shouldShowCustomerSecretProviderSettings,
        shouldShowEmailSourceSettings,
        shouldShowExternalAccountSettings,
        shouldShowQuickReport,
        shouldShowSettingsPage,
        shouldShowWebhookSettings: flags[FeatureFlag.manualWebhooks] ?? false,
      },
      registrations
    )
  )
  // A runtime-registered controller overrides the registration's static
  // dirtyState; sections without one keep whatever the registration declared.
  const sections = catalogSections.map((section) => {
    const controller = dirtyStateControllers[section.id]
    return controller ? { ...section, dirtyState: controller } : section
  })

  const sectionQuery = Array.isArray(router.query.section)
    ? router.query.section[0]
    : router.query.section
  const activeSection =
    sections.find((section) => section.id === sectionQuery) ??
    sections.find((section) => section.id === DEFAULT_SETTINGS_SECTION) ??
    sections[0]
  const hasUnsavedChanges = activeSection.dirtyState?.hasUnsavedChanges ?? false

  useEffect(() => {
    if (
      !router.isReady ||
      sectionQuery === undefined ||
      sectionQuery === activeSection.id
    ) {
      return
    }

    const { section: _section, ...query } = router.query
    void router.replace({ pathname: SETTINGS_PATH, query }, undefined, {
      shallow: true,
    })
  }, [activeSection.id, router, sectionQuery])

  const navigateToSection = useCallback(
    (sectionId: SettingsSectionId) => {
      const { section: _section, ...query } = router.query

      if (sectionId !== DEFAULT_SETTINGS_SECTION) {
        query.section = sectionId
      }

      void router.push({ pathname: SETTINGS_PATH, query }, undefined, {
        shallow: true,
      })
    },
    [router]
  )

  const selectSection = (sectionId: SettingsSectionId) => {
    if (sectionId === activeSection.id) {
      return
    }

    if (hasUnsavedChanges) {
      setPendingNavigation({ sectionId, type: 'section' })
      return
    }

    navigateToSection(sectionId)
  }

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNavigationRef.current) {
        return
      }

      event.preventDefault()
      event.returnValue = ''
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        allowNavigationRef.current ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return
      }

      const target = event.target
      const anchor =
        target instanceof Element
          ? target.closest<HTMLAnchorElement>('a')
          : null

      if (!anchor?.href || anchor.download || anchor.target === '_blank') {
        return
      }

      const destination = new URL(anchor.href)
      if (
        destination.origin === window.location.origin &&
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      const url = destination.href.startsWith(window.location.origin)
        ? `${destination.pathname}${destination.search}${destination.hash}`
        : anchor.href
      setPendingNavigation({ source: 'link', type: 'route', url })
    }

    const returnState = window.history.state
    const returnUrl = router.asPath
    router.beforePopState(({ as }) => {
      if (allowNavigationRef.current) {
        return true
      }

      setPendingNavigation({
        returnState,
        returnUrl,
        source: 'history',
        type: 'route',
        url: as,
      })
      return false
    })
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('click', handleDocumentClick, true)

    return () => {
      router.beforePopState(() => true)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleDocumentClick, true)
    }
  }, [hasUnsavedChanges, router, router.asPath])

  const continueNavigation = () => {
    if (!pendingNavigation) {
      return
    }

    const navigation = pendingNavigation
    setPendingNavigation(undefined)
    allowNavigationRef.current = true

    if (navigation.type === 'section') {
      navigateToSection(navigation.sectionId)
      allowNavigationRef.current = false
      return
    }

    const navigationResult =
      navigation.source === 'history'
        ? router.replace(navigation.url)
        : router.push(navigation.url)
    void Promise.resolve(navigationResult).finally(() => {
      allowNavigationRef.current = false
    })
  }

  const keepEditing = () => {
    if (
      pendingNavigation?.type === 'route' &&
      pendingNavigation.source === 'history'
    ) {
      window.history.pushState(
        pendingNavigation.returnState,
        '',
        pendingNavigation.returnUrl
      )
    }

    setPendingNavigation(undefined)
  }

  const discardAndContinue = () => {
    activeSection.dirtyState?.discard()
    continueNavigation()
  }

  const saveAndContinue = async () => {
    const didSave = await activeSection.dirtyState?.save()
    if (didSave) {
      continueNavigation()
    }
  }

  return {
    activeSection,
    discardAndContinue,
    isNavigationPending: pendingNavigation !== undefined,
    keepEditing,
    registerDirtyState,
    saveAndContinue,
    sections,
    selectSection,
  }
}
